"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AeraButton } from "@aera/ui";
import type { Transaction } from "@aera/data-table";
import { createMarketApiFetch } from "@/lib/marketApiFetch";
import { resolveOwnerUserId } from "@/lib/pendingOrders";
import type { ReceivableListItem } from "@/lib/pendingOrders";
import {
  MoveAllocationModal,
  ResplitAllocationModal,
} from "@/app/artifacts/table/ReceivableMoveResplitModals";
import { offerMoneyUndoToast } from "@/lib/moneyUndoToast";
import { INCOME_MONEY_CHANGED_EVENT } from "@/lib/incomeMoneyRefresh";
import { toFriendlyThaiErrorMessage } from "@/lib/friendlyThaiErrorMessage";
import { fetchReceivableDebtsDetails } from "@/lib/pendingOrders";
import { filterReceivableMoneyCandidates } from "@/lib/receivableDisplayStatus";

const marketApiFetch = createMarketApiFetch();

type MoneyLinkAllocation = {
  allocation_id: string;
  receivable_id: string;
  amount: string;
  customer_name?: string;
  receivable_revision?: number;
  receivable_status?: string;
  receivable_amount?: string;
  amount_paid?: string;
};

type MoneyLink = {
  ok?: boolean;
  reason?: string;
  payment_id?: string;
  allocation_id?: string;
  receivable_id?: string;
  payment_amount?: string;
  allocated_amount?: string;
  unallocated_amount?: string;
  allocations?: MoneyLinkAllocation[];
};

function getRecordIdText(record: Transaction) {
  const raw = record.record_id as unknown;
  if (raw && typeof raw === "object" && "value" in raw) {
    const value = (raw as { value?: unknown }).value;
    return value == null ? "" : String(value);
  }
  return raw == null ? "" : String(raw);
}

function formatBaht(value: string | undefined): string {
  const n = Number(String(value ?? "").replace(/,/g, "").trim());
  if (!Number.isFinite(n)) return value?.trim() || "—";
  return n.toLocaleString("th-TH", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function receivableStatusLabel(status: string | undefined): string {
  switch ((status || "").trim()) {
    case "closed":
      return "ปิดแล้ว";
    case "partially_paid":
      return "จ่ายบางส่วน";
    case "open":
      return "เปิดค้าง";
    default:
      return status?.trim() || "—";
  }
}

function SkeletonBar({ className }: { className: string }) {
  return (
    <div
      className={`animate-pulse rounded-md bg-aera-lightgray/80 ${className}`}
      aria-hidden
    />
  );
}

function IncomeMoneyActionsSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div className="space-y-2" aria-busy="true" aria-live="polite">
      <SkeletonBar className="h-3 w-40" />
      <ul className="divide-y divide-(--aera-dt-border-soft) overflow-hidden rounded-xl border border-(--aera-dt-border-soft) bg-aera-offwhite">
        {Array.from({ length: rows }, (_, index) => (
          <li
            key={index}
            className="flex items-start justify-between gap-3 px-3 py-2.5"
          >
            <div className="min-w-0 flex-1 space-y-1.5">
              <SkeletonBar className="h-3.5 w-24" />
              <SkeletonBar className="h-2.5 w-40 max-w-full" />
            </div>
            <SkeletonBar className="mt-0.5 h-3.5 w-10 shrink-0" />
          </li>
        ))}
      </ul>
      <SkeletonBar className="h-2.5 w-52 max-w-full" />
      <span className="sr-only">กำลังโหลดการจัดสรร</span>
    </div>
  );
}

export function IncomeMoneyActions({
  record,
  workspaceId,
  onApplied,
  onAllocationCountChange,
  allocationTabActive = false,
  readOnly = false,
}: {
  record: Transaction;
  workspaceId: string;
  onApplied?: () => void | Promise<void>;
  onAllocationCountChange?: (count: number) => void;
  /** When true, silent-reload money-link (tab stays mounted while hidden). */
  allocationTabActive?: boolean;
  /** Role cannot write (caller's can()) — move / resplit buttons disabled. */
  readOnly?: boolean;
}) {
  const recordId = useMemo(() => getRecordIdText(record), [record]);
  const ownerUserId = resolveOwnerUserId();
  const [link, setLink] = useState<MoneyLink | null>(null);
  // Start true so the first paint (and remount via record key) shows skeleton
  // instead of a flash of the empty/error copy before loadLink runs.
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [moveOpen, setMoveOpen] = useState(false);
  const [resplitOpen, setResplitOpen] = useState(false);
  const [openingAction, setOpeningAction] = useState<"move" | "resplit" | null>(
    null,
  );
  const [destinations, setDestinations] = useState<ReceivableListItem[]>([]);
  const [selectedAllocationId, setSelectedAllocationId] = useState<string>("");
  /** Blocks reopening move/resplit while post-submit refresh is in flight. */
  const [actionLock, setActionLock] = useState(false);
  /** Snapshot while move modal is open — survives allocation list refresh after submit. */
  const [moveSession, setMoveSession] = useState<{
    allocationId: string;
    amount: string;
    paymentId: string;
    sourceLabel: string;
    sourceItem: ReceivableListItem;
  } | null>(null);
  /** Run table resetQueries only after move/resplit modal has closed (avoids unmount flash). */
  const pendingTableRefreshRef = useRef(false);
  const moveSessionRef = useRef(moveSession);
  moveSessionRef.current = moveSession;
  const resplitOpenRef = useRef(resplitOpen);
  resplitOpenRef.current = resplitOpen;

  const loadLink = useCallback(async (opts?: { silent?: boolean }) => {
    if (!recordId || !workspaceId.trim()) {
      setLink(null);
      setLoading(false);
      onAllocationCountChange?.(0);
      return null;
    }
    if (!opts?.silent) setLoading(true);
    setError(null);
    try {
      const res = await marketApiFetch(
        `/api/v1/transactions/${encodeURIComponent(recordId)}/money-link?workspace_id=${encodeURIComponent(workspaceId)}`,
        { headers: { Accept: "application/json" } },
      );
      const data = (await res.json().catch(() => ({}))) as MoneyLink;
      if (!res.ok) {
        throw new Error(
          typeof (data as { detail?: string }).detail === "string"
            ? (data as { detail: string }).detail
            : `โหลดลิงก์เงินไม่สำเร็จ (${res.status})`,
        );
      }
      setLink(data);
      const preferred =
        data.allocations?.find((a) => a.allocation_id === data.allocation_id)
          ?.allocation_id ??
        data.allocations?.[0]?.allocation_id ??
        "";
      setSelectedAllocationId((prev) => {
        if (prev && data.allocations?.some((a) => a.allocation_id === prev)) {
          return prev;
        }
        return preferred;
      });
      onAllocationCountChange?.(data.allocations?.length ?? 0);
      return data;
    } catch (err) {
      setLink(null);
      onAllocationCountChange?.(0);
      setError(
        err instanceof Error
          ? toFriendlyThaiErrorMessage(err.message) || "โหลดไม่สำเร็จ"
          : "โหลดไม่สำเร็จ",
      );
      return null;
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  }, [onAllocationCountChange, recordId, workspaceId]);

  useEffect(() => {
    void loadLink();
  }, [loadLink]);

  // Reload when user returns to การจัดสรร after undo/move elsewhere in the drawer.
  useEffect(() => {
    if (!allocationTabActive) return;
    void loadLink({ silent: true });
  }, [allocationTabActive, loadLink]);

  useEffect(() => {
    const onMoneyChanged = (event: Event) => {
      // Don't churn money-link (or risk modal remount) while a money modal is open.
      if (moveSessionRef.current || resplitOpenRef.current) return;
      const detail = (event as CustomEvent<{ recordId?: string }>).detail;
      const targetId = detail?.recordId?.trim();
      if (targetId && targetId !== recordId) return;
      void loadLink({ silent: true });
    };
    window.addEventListener(INCOME_MONEY_CHANGED_EVENT, onMoneyChanged);
    return () => {
      window.removeEventListener(INCOME_MONEY_CHANGED_EVENT, onMoneyChanged);
    };
  }, [loadLink, recordId]);

  const loadDestinations = useCallback(async () => {
    if (!ownerUserId || !workspaceId.trim()) return;
    try {
      // Pending/review are workspace-scoped: omitting workspace_id returns only the
      // `_none` bucket, not all of this owner's orders.
      const details = await fetchReceivableDebtsDetails(ownerUserId, {
        workspaceId,
      });
      setDestinations(
        filterReceivableMoneyCandidates(
          details.lists?.byKind?.announcement ?? [],
        ),
      );
    } catch {
      setDestinations([]);
    }
  }, [ownerUserId, workspaceId]);

  const buildMoveSession = useCallback(
    (data: MoneyLink, preferredAllocationId: string) => {
      const paymentId = data.payment_id?.trim() || "";
      if (!paymentId || !data.allocations?.length) return null;
      const alloc =
        data.allocations.find((a) => a.allocation_id === preferredAllocationId) ??
        data.allocations.find((a) => a.allocation_id === data.allocation_id) ??
        data.allocations[0];
      if (!alloc) return null;
      return {
        allocationId: alloc.allocation_id,
        amount: alloc.amount,
        paymentId,
        sourceLabel: alloc.customer_name?.trim() || "",
        sourceItem: {
          key: alloc.receivable_id,
          mergeKey: alloc.receivable_id,
          title: alloc.customer_name || alloc.receivable_id,
          receivableId: alloc.receivable_id,
          fields: [
            { key: "receivable_id", label: "id", value: alloc.receivable_id },
            {
              key: "revision",
              label: "revision",
              value: String(alloc.receivable_revision ?? 0),
            },
          ],
        } as ReceivableListItem,
      };
    },
    [],
  );

  const openMoneyModal = useCallback(
    async (action: "move" | "resplit") => {
      if (openingAction || actionLock) return;
      setOpeningAction(action);
      setError(null);
      try {
        const data = await loadLink({ silent: true });
        await loadDestinations();
        if (action === "move") {
          if (!data?.ok || !data.payment_id) {
            setError("ยังไม่สามารถย้ายยอดจากรายการนี้ได้");
            return;
          }
          const session = buildMoveSession(data, selectedAllocationId);
          if (!session) {
            setError("ไม่พบรายการจัดสรรให้ย้าย");
            return;
          }
          setMoveSession(session);
          setMoveOpen(true);
        } else {
          setResplitOpen(true);
        }
      } catch (err) {
        setError(
          err instanceof Error
            ? toFriendlyThaiErrorMessage(err.message) || "เปิดหน้าต่างไม่สำเร็จ"
            : "เปิดหน้าต่างไม่สำเร็จ",
        );
      } finally {
        setOpeningAction(null);
      }
    },
    [
      actionLock,
      buildMoveSession,
      loadDestinations,
      loadLink,
      openingAction,
      selectedAllocationId,
    ],
  );

  const allocations = link?.allocations ?? [];
  const resplitCurrentAllocations = useMemo(
    () =>
      allocations.map((a) => {
        const receivableTotal = Number(
          String(a.receivable_amount ?? "").replace(/,/g, "").trim(),
        );
        const paid = Number(String(a.amount_paid ?? "").replace(/,/g, "").trim());
        let remainingLabel: string | undefined;
        if (Number.isFinite(receivableTotal) && Number.isFinite(paid)) {
          remainingLabel = formatBaht(
            String(Math.max(0, receivableTotal - paid)),
          );
        }
        return {
          allocation_id: a.allocation_id,
          receivable_id: a.receivable_id,
          amount: a.amount,
          label: a.customer_name,
          remainingLabel,
        };
      }),
    [allocations],
  );
  const activeAlloc =
    allocations.find((a) => a.allocation_id === selectedAllocationId) ??
    allocations.find((a) => a.allocation_id === link?.allocation_id) ??
    allocations[0];
  const canAct = Boolean(
    !readOnly &&
    link?.ok &&
      link.payment_id &&
      allocations.length > 0 &&
      ownerUserId &&
      workspaceId,
  );
  const buttonsBusy =
    openingAction != null || moveOpen || resplitOpen || actionLock;
  const skeletonRows = Math.max(allocations.length, 3);
  const totalAllocated = allocations.reduce((sum, row) => {
    const n = Number(String(row.amount ?? "").replace(/,/g, ""));
    return sum + (Number.isFinite(n) ? n : 0);
  }, 0);

  if (!recordId) return null;

  // Opening a money modal, initial load, or post-save soft refresh.
  const showPanelSkeleton =
    openingAction != null || loading || actionLock;

  return (
    <div className="space-y-3 rounded-2xl border border-(--aera-dt-border-soft) bg-(--aera-dt-surface-elevated) p-4">
      <h3 className="text-sm font-bold text-aera-charcoal">การจัดสรรเงินก้อนนี้</h3>

      {showPanelSkeleton ? (
        <>
          <IncomeMoneyActionsSkeleton rows={skeletonRows} />
          <div className="flex flex-wrap gap-2" aria-hidden>
            <SkeletonBar className="h-8 w-20 rounded-xl" />
            <SkeletonBar className="h-8 w-24 rounded-xl" />
          </div>
          <p className="sr-only">กำลังอัปเดตการจัดสรร</p>
        </>
      ) : link?.ok ? (
        <div className="space-y-2">
          <p className="text-xs text-aera-gray">
            จับคู่ {allocations.length} รายการ · รวม{" "}
            <span className="font-semibold tabular-nums text-aera-charcoal">
              {formatBaht(String(totalAllocated))}
            </span>{" "}
            บาท
          </p>
          {allocations.length > 0 ? (
            <ul className="divide-y divide-(--aera-dt-border-soft) overflow-hidden rounded-xl border border-(--aera-dt-border-soft) bg-aera-offwhite">
              {allocations.map((row) => {
                return (
                  <li key={row.allocation_id}>
                    <button
                      type="button"
                      className="flex w-full items-start justify-between gap-3 px-3 py-2.5 text-left transition-colors hover:bg-(--aera-dt-row-selected-surface)"
                      onClick={() => setSelectedAllocationId(row.allocation_id)}
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-aera-charcoal">
                          {row.customer_name?.trim() || "ไม่ระบุชื่อ"}
                        </p>
                        <p className="mt-0.5 text-[11px] text-aera-lightslate">
                          {receivableStatusLabel(row.receivable_status)}
                          {row.receivable_amount
                            ? ` · ยอดค้าง ${formatBaht(row.receivable_amount)}`
                            : ""}
                          {row.amount_paid
                            ? ` · รับแล้ว ${formatBaht(row.amount_paid)}`
                            : ""}
                        </p>
                      </div>
                      <p className="shrink-0 text-sm font-bold tabular-nums text-aera-charcoal">
                        {formatBaht(row.amount)}
                      </p>
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-xs text-aera-lightslate">ยังไม่มีการจัดสรรที่ยังใช้งาน</p>
          )}
          <p className="text-[11px] leading-relaxed text-aera-lightslate">
            ดูประวัติจับคู่/ย้ายยอดได้ที่แท็บประวัติ
          </p>
        </div>
      ) : (
        <p className="text-xs text-aera-lightslate">
          {link?.reason === "no_payment_link"
            ? "รายรับนี้ยังไม่ผูกกับ Payment ที่จัดสรรได้"
            : error || "ยังไม่สามารถจัดการจัดสรรจากรายการนี้ได้"}
        </p>
      )}

      {!showPanelSkeleton ? (
        <>
          <div className="flex flex-wrap gap-2">
            <AeraButton
              type="button"
              variant="secondary"
              size="sm"
              disabled={!canAct || !activeAlloc || buttonsBusy || loading}
              onClick={() => {
                void openMoneyModal("move");
              }}
            >
              ย้ายยอด
            </AeraButton>
            <AeraButton
              type="button"
              variant="primary"
              size="sm"
              disabled={!canAct || buttonsBusy || loading}
              onClick={() => {
                void openMoneyModal("resplit");
              }}
            >
              จัดสรรใหม่
            </AeraButton>
          </div>
          <p className="text-[11px] leading-relaxed text-aera-lightslate">
          · ย้ายยอด = เปลี่ยนไปยอดค้างอื่นทั้งก้อน (ตามยอดค้างปลายทาง ·
            ส่วนเกินกลับรอจัดสรร) <br /> · จัดสรรใหม่ = แบ่ง/ปรับหลายรายการ
          </p>
        </>
      ) : null}

      {error && link?.ok !== false && !showPanelSkeleton ? (
        <p className="text-xs font-medium text-aera-red">{error}</p>
      ) : null}

      {moveSession ? (
        <MoveAllocationModal
          open={moveOpen}
          onOpenChange={setMoveOpen}
          onExited={() => {
            setMoveSession(null);
            if (!pendingTableRefreshRef.current) return;
            pendingTableRefreshRef.current = false;
            setActionLock(true);
            void (async () => {
              try {
                await onApplied?.();
                await loadLink({ silent: true });
              } finally {
                setActionLock(false);
              }
            })();
          }}
          sourceItem={moveSession.sourceItem}
          allocationId={moveSession.allocationId}
          amount={moveSession.amount}
          paymentId={moveSession.paymentId}
          destinationCandidates={destinations}
          ownerUserId={ownerUserId}
          workspaceId={workspaceId}
          onMoved={async (operationId) => {
            if (ownerUserId) {
              offerMoneyUndoToast({
                message: "ย้ายยอดสำเร็จ",
                sourceLabel: moveSession.sourceLabel || undefined,
                ownerUserId,
                workspaceId,
                operationId,
                onUndone: onApplied,
              });
            }
            // Soft refresh only — resetQueries while the modal is open remounts the
            // drawer tree and the portal flickers away then back.
            pendingTableRefreshRef.current = true;
            await loadLink({ silent: true });
          }}
        />
      ) : null}

      {link?.payment_id ? (
        <ResplitAllocationModal
          open={resplitOpen}
          onOpenChange={(next) => {
            setResplitOpen(next);
            if (next || !pendingTableRefreshRef.current) return;
            pendingTableRefreshRef.current = false;
            setActionLock(true);
            void (async () => {
              try {
                await onApplied?.();
                await loadLink({ silent: true });
              } finally {
                setActionLock(false);
              }
            })();
          }}
          paymentId={link.payment_id}
          paymentRemainingLabel={link.unallocated_amount || "0"}
          currentAllocations={resplitCurrentAllocations}
          receivableCandidates={destinations}
          ownerUserId={ownerUserId}
          workspaceId={workspaceId}
          onResplit={async (info) => {
            if (ownerUserId) {
              offerMoneyUndoToast({
                message: info.label,
                ownerUserId,
                workspaceId,
                operationId: info.operationId,
                onUndone: onApplied,
              });
            }
            pendingTableRefreshRef.current = true;
            await loadLink({ silent: true });
          }}
        />
      ) : null}
    </div>
  );
}
